
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Backdrop, CircularProgress } from '@mui/material';
import { httpService } from '../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../services/api';
import { getSessionKey, setSessionKey, clearSessionKey } from '../services/sessionKey';
import { clearToken, clearUserDetails } from '../features/auth/authSlice';
import { decryptResponse } from '../services';

interface Props {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch();
  const token = useSelector((state: any) => state.auth.accessToken);
  const isResetPassword = useSelector((state: any) => state.auth?.userDetails?.is_reset_password);

  // After a page refresh the in-memory session key is lost while the auth
  // token is still persisted. Restore the session key from the backend
  // (authorized by the httpOnly session cookie) before rendering.
  const [restoring, setRestoring] = useState<boolean>(Boolean(token) && !getSessionKey());

  useEffect(() => {
    let active = true;

    const restore = async () => {
      if (!token || getSessionKey()) {
        if (active) setRestoring(false);
        return;
      }

      setRestoring(true);
      try {
        const res = await httpService.post<any>(`${API_BASE_URL}/user/restoreAdminSession`, {});
        const decryptedData = await decryptResponse(res.data.data, REQ_ENC_KEY);
        const key = (decryptedData as any)?.session_key;
        if (!active) return;
        if (key) {
          setSessionKey(key);
        } else {
          clearSessionKey();
          dispatch(clearToken());
          dispatch(clearUserDetails());
        }
      } catch {
        if (!active) return;
        clearSessionKey();
        dispatch(clearToken());
        dispatch(clearUserDetails());
      } finally {
        if (active) setRestoring(false);
      }
    };

    restore();
    return () => {
      active = false;
    };
  }, [token, dispatch]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (restoring) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!isResetPassword) {
    return <Navigate to="/reset-password" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
